import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { LogOut, Sun, Moon, MapPin, ChevronDown, HelpCircle, ShieldCheck, UserCog, Rocket, Search, CreditCard } from "lucide-react";
import Logo from "./Logo";
import DynamicIntelligencePicker from "./DynamicIntelligencePicker";
import SpotlightSearch from "./SpotlightSearch";
import { AccessStatusPill, RedeemCodeModal, useAccessState } from "./AccessStatusPanel";
import api, { captureUserLocation, getCachedLocation, setCachedLocation } from "../lib/api";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const ACTIVE_ORG_KEY = "sp:active-org";

export default function TopBar({ children, right = null }) {
  const { user, logout } = useAuth();
  const { mode, toggle } = useTheme();
  const nav = useNavigate();
  const location = useLocation();
  const [loc, setLoc] = useState(getCachedLocation());
  const [searchOpen, setSearchOpen] = useState(false);
  // Time-bounded access pill + modal. `useAccessState` is a no-op for
  // perpetual / superadmin / >14-day users — they see nothing.
  const { state: accessState, usage: accessUsage, refresh: refreshAccess } = useAccessState();
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [activeOrgId, setActiveOrgId] = useState(() => {
    try { return window.localStorage.getItem(ACTIVE_ORG_KEY) || ""; }
    catch { return ""; }
  });

  useEffect(() => {
    // Re-read on mount in case another tab updated localStorage
    setLoc(getCachedLocation());
  }, []);

  // Track active org from URL (/app/org/:id/...) and persist for use
  // when on routes that don't carry the org id (e.g. /app/team/:id).
  useEffect(() => {
    const m = location.pathname.match(/\/app\/org\/([a-f0-9]{24})/i);
    if (m) {
      setActiveOrgId(m[1]);
      try { window.localStorage.setItem(ACTIVE_ORG_KEY, m[1]); } catch {}
    }
  }, [location.pathname]);

  // Fallback: if no active org is known yet but the user is logged in,
  // pull /api/orgs and pick the first one.
  useEffect(() => {
    if (activeOrgId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/orgs");
        if (!cancelled && Array.isArray(data) && data[0]) {
          setActiveOrgId(data[0].id);
          try { window.localStorage.setItem(ACTIVE_ORG_KEY, data[0].id); } catch {}
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [activeOrgId, user]);

  // ⌘K / Ctrl+K global shortcut for search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const askLocation = async () => {
    if (loc) {
      // Already on — clicking again clears the consent
      setCachedLocation("");
      setLoc("");
      toast.success("Location turned off");
      return;
    }
    const result = await captureUserLocation();
    if (result) { setLoc(result); toast.success(`Location: ${result}`); }
    else toast.error("Location unavailable or denied");
  };
  return (
    <header
      className="sticky top-0 z-40 border-b"
      style={{
        borderColor: "var(--border-soft)",
        // Pad for the iPhone notification bar when installed as a PWA. In a
        // regular browser window env(safe-area-inset-top) resolves to 0, so
        // this has no effect outside standalone mode.
        paddingTop: "env(safe-area-inset-top)",
        // Mostly-opaque background with a strong blur so content scrolling
        // under the frozen bar is clearly separated without vanishing behind
        // it — the old 88% translucency made the bar feel unanchored.
        background: "color-mix(in srgb, var(--bg) 96%, transparent)",
        backdropFilter: "blur(28px) saturate(160%)",
        WebkitBackdropFilter: "blur(28px) saturate(160%)",
        boxShadow: "0 1px 0 0 rgba(0,0,0,0.04), 0 8px 24px -16px rgba(0,0,0,0.25)",
      }}
    >
      <div>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3 gap-2 sm:gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/app" className="flex items-center gap-2 shrink-0" data-testid="topbar-brand" aria-label="Foundry home">
              {/* Compact mark on mobile, full wordmark from sm: up so it
                  matches the landing & auth screens. */}
              <Logo variant="mark" className="w-9 h-9 sm:hidden" />
              <Logo variant="wordmark" className="hidden sm:block h-8 w-auto" />
            </Link>
            <div className="h-5 w-px mx-1 hidden sm:block" style={{ background: "var(--border-soft)" }} />
            <div className="truncate text-sm sp-subtle">{children}</div>
          </div>
          <div className="flex items-center gap-2">
            {/* Trial countdown — only renders when the signed-in user
                has an active 7-day trial. Disappears the moment the
                clock runs out so it doesn't haunt expired-trial users
                with stale info. */}
            {user && <TrialCountdownBadge user={user} />}
            {right}
            {/* Global Dynamic Intelligence indicator. The wrapper used
                to enforce `max-w-[180px]` with `overflow-hidden`, but
                after the +20% global type scale the label "Dynamic
                Intelligence" overflows 180px and the overflow-hidden
                also clipped the dropdown panel when opened. We now let
                the chip size naturally and rely on the topbar's flex
                gap + the responsive `hidden sm:block` to keep things
                tidy on narrow viewports. */}
            {user && (
              <div className="hidden sm:block whitespace-nowrap" data-testid="topbar-dynamic-intelligence">
                <DynamicIntelligencePicker compact />
              </div>
            )}
            {/* Spotlight search trigger */}
            <button
              onClick={() => setSearchOpen(true)}
              className="sp-btn sp-btn-ghost text-xs flex items-center gap-1.5"
              data-testid="topbar-search"
              aria-label="Search"
              title="Search (⌘K)"
            >
              <Search className="w-4 h-4" />
              <kbd className="hidden lg:inline text-[10px] sp-subtle font-mono">⌘K</kbd>
            </button>
            {/* Access-window pill — only renders for users with a
                limited window AND ≤14 days remaining (or in grace /
                blocked). Click → opens the Redeem modal. */}
            <AccessStatusPill state={accessState} usage={accessUsage} onOpen={() => setRedeemOpen(true)} />
            {/* Icon-only theme toggle — label removed to reclaim space. */}
            <button
              onClick={toggle}
              className="sp-btn sp-btn-ghost text-xs"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
              title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {mode === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="sp-btn sp-btn-ghost text-xs"
                  data-testid="topbar-user-menu"
                  title="Account menu"
                >
                  <span className="hidden md:inline-flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400/80" />
                    <span className="truncate max-w-[140px]">{user?.name || user?.email}</span>
                    <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                  </span>
                  <span className="md:hidden inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-400/80" />
                    <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[220px]"
                data-testid="topbar-user-menu-content"
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm">{user?.name || "Signed in"}</span>
                    {user?.email && (
                      <span className="text-xs sp-subtle truncate">{user.email}</span>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => nav("/app/account")}
                  data-testid="topbar-menu-account"
                  className="cursor-pointer"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Account settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => nav("/app/billing")}
                  data-testid="topbar-menu-billing"
                  className="cursor-pointer"
                >
                  <CreditCard className="w-4 h-4 mr-2 text-orange-300" />
                  Plan &amp; billing
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => nav("/app/audits")}
                  data-testid="topbar-menu-audits"
                  className="cursor-pointer"
                >
                  <Rocket className="w-4 h-4 mr-2 text-orange-300" />
                  Run a Signal Audit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {/* Moved from top-bar surface to the menu (UX audit #1):
                    location toggle + About link live here so the nav only
                    carries essentials. */}
                <DropdownMenuItem
                  onSelect={askLocation}
                  data-testid="topbar-menu-location"
                  className="cursor-pointer"
                >
                  <MapPin className={`w-4 h-4 mr-2 ${loc ? "text-orange-400" : ""}`} />
                  {loc ? `Location · ${loc}` : "Share my location"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {user?.is_superadmin && (
                  <>
                    <DropdownMenuItem
                      onSelect={() => nav("/admin")}
                      data-testid="topbar-menu-admin"
                      className="cursor-pointer"
                    >
                      <ShieldCheck className="w-4 h-4 mr-2 text-emerald-400" />
                      Admin dashboard
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onSelect={() => nav("/about")}
                  data-testid="topbar-menu-about"
                  className="cursor-pointer"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  What is Foundry?
                </DropdownMenuItem>
                {/* Trust & Security — same dropdown slot as About so
                    it's discoverable for in-app users (and easy to show
                    a prospect during a screen-share demo) without
                    cluttering the working top bar. */}
                <DropdownMenuItem
                  onSelect={() => nav("/trust")}
                  data-testid="topbar-menu-trust"
                  className="cursor-pointer"
                >
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Trust &amp; Security
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={async () => { await logout(); nav("/"); }}
                  data-testid="topbar-menu-logout"
                  className="cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      <SpotlightSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        orgId={activeOrgId}
      />
      <RedeemCodeModal
        open={redeemOpen}
        onClose={() => setRedeemOpen(false)}
        onRedeemed={() => refreshAccess()}
        currentState={accessState}
      />
    </header>
  );
}


// ── Trial countdown badge ──────────────────────────────────────────────────


function TrialCountdownBadge({ user }) {
  // Use a tick to recompute the relative remaining time once a minute.
  // Re-rendering the whole TopBar every second would be wasteful; per
  // minute is granular enough for a 7-day countdown.
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const expIso = user?.trial_expires_at;
  if (!expIso) return null;
  const exp = new Date(expIso).getTime();
  if (Number.isNaN(exp)) return null;
  const remaining = exp - Date.now();
  if (remaining <= 0) return null;  // expired — hide; an upsell would go here once billing exists.
  const hours = Math.floor(remaining / 3_600_000);
  const minutes = Math.floor((remaining % 3_600_000) / 60_000);
  const days = Math.floor(hours / 24);
  const label = days >= 1 ? `${days}d ${hours % 24}h left` : hours >= 1 ? `${hours}h left` : `${minutes}m left`;
  // Color tilts orange under 4 hours so the user feels the urgency.
  const color = hours < 4 ? "rgba(251,146,60,1)" : "rgba(74,222,128,1)";
  const bg = hours < 4 ? "rgba(251,146,60,0.12)" : "rgba(74,222,128,0.12)";
  return (
    <Link
      to="/trust"
      className="hidden md:inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium transition hover:brightness-125"
      style={{ background: bg, color, border: `1px solid ${bg.replace("0.12", "0.3")}` }}
      title="Your 7-day trial is active — make the most of it"
      data-testid="topbar-trial-badge"
    >
      <Rocket className="w-3 h-3" />
      Trial · {label}
    </Link>
  );
}
